// src/modules/goldInventory/pages/InvoiceSettings.jsx
// Editor for the company's own SELLER details, BANK details, TERMS & CONDITIONS
// and DECLARATION used on smelter sale tax invoices (and their PDF).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Typography, Form, Input, Button, Row, Col, Space, Spin, message, Divider, InputNumber,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { getInvoiceProfile, updateInvoiceProfile } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;

export default function InvoiceSettings() {
  const navigate = useNavigate();
  const { canEditModule } = usePermissions();
  const readOnly = !canEditModule('goldInventory');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    getInvoiceProfile()
      .then((r) => {
        const p = r.data || {};
        form.setFieldsValue({
          seller: p.seller || {},
          bank: p.bank || {},
          goldGstRate: p.goldGstRate ?? 3,
          terms: (p.terms && p.terms.length) ? p.terms : [''],
          declaration: p.declaration || '',
        });
      })
      .catch(() => messageApi.error('Failed to load invoice profile'))
      .finally(() => setLoading(false));
  }, []);

  const onSave = async (values) => {
    setSaving(true);
    try {
      await updateInvoiceProfile({
        ...values,
        terms: (values.terms || []).filter((t) => (t || '').trim() !== ''),
      });
      messageApi.success('Invoice details saved');
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin style={{ display: 'block', margin: 80, textAlign: 'center' }} />;

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      {contextHolder}
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/gold-inventory')}>Back</Button>
          <Title level={4} style={{ margin: 0 }}>Company / Invoice Details</Title>
        </Space>
        {!readOnly && (
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
            Save
          </Button>
        )}
      </Row>

      <Text type="secondary">
        These details appear as the SELLER, BANK, TERMS &amp; CONDITIONS and DECLARATION on every smelter sale tax invoice PDF.
      </Text>

      <Form form={form} layout="vertical" onFinish={onSave} disabled={readOnly} style={{ marginTop: 16 }}>
        <Card title="Seller Details" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={12}>
            <Col xs={24} md={16}><Form.Item label="Company Name" name={['seller', 'name']} rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item label="GST Rate on Gold (%)" name="goldGstRate"><InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item label="GSTIN" name={['seller', 'gstin']}><Input /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item label="PAN" name={['seller', 'pan']}><Input /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item label="CIN" name={['seller', 'cin']}><Input /></Form.Item></Col>
            <Col xs={24} md={16}><Form.Item label="Address" name={['seller', 'address']}><Input.TextArea rows={2} /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item label="State" name={['seller', 'state']}><Input placeholder="e.g. West Bengal" /></Form.Item></Col>
            <Col xs={12} md={8}><Form.Item label="State Code" name={['seller', 'stateCode']}><Input placeholder="e.g. 19" /></Form.Item></Col>
            <Col xs={12} md={8}><Form.Item label="Phone" name={['seller', 'phone']}><Input /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item label="Email" name={['seller', 'email']}><Input /></Form.Item></Col>
          </Row>
        </Card>

        <Card title="Bank Details" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={12}>
            <Col xs={24} md={12}><Form.Item label="Bank Name" name={['bank', 'bankName']}><Input /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item label="Account Name" name={['bank', 'accountName']}><Input /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item label="Account No." name={['bank', 'accountNo']}><Input /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item label="IFSC" name={['bank', 'ifsc']}><Input /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item label="Payment Terms" name={['bank', 'paymentTerms']}><Input placeholder="e.g. Immediate RTGS / NEFT" /></Form.Item></Col>
          </Row>
        </Card>

        <Card title="Terms & Conditions" size="small" style={{ marginBottom: 16 }}>
          <Form.List name="terms">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, idx) => (
                  <Row key={field.key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                    <Col flex="24px"><Text type="secondary">{idx + 1}.</Text></Col>
                    <Col flex="auto">
                      <Form.Item name={field.name} style={{ margin: 0 }}>
                        <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} placeholder="Term / condition text" />
                      </Form.Item>
                    </Col>
                    <Col flex="32px">
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} disabled={readOnly} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add('')} disabled={readOnly} style={{ width: '100%' }}>
                  Add term
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card title="Declaration" size="small">
          <Form.Item name="declaration" style={{ margin: 0 }}>
            <Input.TextArea rows={3} placeholder="Declaration text shown on the invoice" />
          </Form.Item>
        </Card>

        <Divider />
        {!readOnly && (
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
            Save Invoice Details
          </Button>
        )}
      </Form>
    </div>
  );
}
